import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import SchemaMarkup from './SchemaMarkup';

const DEFAULT_OG_IMAGE = 'https://raskh.app/share-preview.png';
const TWITTER_SITE = '@raskhapp';

const PageHead = ({
  titleEn,
  titleAr,
  descEn,
  descAr,
  canonical,
  hreflangEn,
  hreflangAr,
  noIndex = false,
  schemas = [],
  lang,
  ogImage,
}) => {
  const { i18n } = useTranslation();
  const resolvedLang = lang || (i18n && i18n.language ? i18n.language : 'en');
  const isArabic = resolvedLang && resolvedLang.toLowerCase().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  const htmlLang = isArabic ? 'ar' : 'en';

  const title = isArabic ? (titleAr || titleEn) : (titleEn || titleAr);
  const description = isArabic ? (descAr || descEn) : (descEn || descAr);
  const ogLocale = isArabic ? 'ar_SA' : 'en_US';
  const ogLocaleAlternate = isArabic ? 'en_US' : 'ar_SA';
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <>
      <Helmet>
        <html lang={htmlLang} dir={dir} />
        {title ? <title>{title}</title> : null}
        {description ? <meta name="description" content={description} /> : null}
        <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        {hreflangEn ? <link rel="alternate" hrefLang="en" href={hreflangEn} /> : null}
        {hreflangAr ? <link rel="alternate" hrefLang="ar" href={hreflangAr} /> : null}
        {hreflangEn ? <link rel="alternate" hrefLang="x-default" href={hreflangEn} /> : null}

        {/* Open Graph */}
        <meta property="og:site_name" content="Raskh" />
        <meta property="og:type" content="website" />
        {title ? <meta property="og:title" content={title} /> : null}
        {description ? <meta property="og:description" content={description} /> : null}
        {canonical ? <meta property="og:url" content={canonical} /> : null}
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={ogLocaleAlternate} />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={TWITTER_SITE} />
        {title ? <meta name="twitter:title" content={title} /> : null}
        {description ? <meta name="twitter:description" content={description} /> : null}
        <meta name="twitter:image" content={image} />
      </Helmet>
      {Array.isArray(schemas) && schemas.filter(Boolean).length > 0 ? (
        <SchemaMarkup
          data={
            schemas.filter(Boolean).length === 1
              ? schemas.filter(Boolean)[0]
              : {
                  '@context': 'https://schema.org',
                  '@graph': schemas
                    .filter(Boolean)
                    .map((s) => {
                      // strip the per-schema @context — the wrapper provides one
                      // eslint-disable-next-line no-unused-vars
                      const { '@context': _ctx, ...rest } = s;
                      return rest;
                    }),
                }
          }
        />
      ) : null}
    </>
  );
};

export default PageHead;
